import {
  Box,
  Card,
  Chip,
  Rating,
  Typography,
  useMediaQuery,
  Stack,
  useTheme,
  Button,
  Fade,
} from "@mui/material";
import React, { useState } from "react";
import {
  LocationOnRounded as LocationIcon,
  ChevronLeftRounded as ChevronLeftIcon,
  FitnessCenterRounded as SportIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

export default function GymCard({ gym }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const [hoveredCard, setHoveredCard] = useState(null);

  // Responsive height based on screen size
  const getCardHeight = () => {
    if (isMobile) return 360;
    if (isTablet) return 400;
    return 440;
  };

  // Get location string
  const getLocationString = () => {
    if (!gym?.location) return "مشهد";
    const cityName = gym.location.city?.name || "";
    const areaNames =
      gym.location.areas?.map((area) => area.name).join("، ") || "";
    return areaNames ? `${areaNames}، ${cityName}` : cityName;
  };

  // Get price
  const getPrice = () => {
    if (!gym?.pricing || gym.pricing.length === 0) return "۰";
    const firstPrice = gym.pricing[0];
    return firstPrice.price?.toLocaleString() || "۰";
  };

  // Get rating
  const getRating = () => gym?.rating_stats?.average_rating || 0;
  const getTotalReviews = () => gym?.rating_stats?.total_reviews || 0;

  // Get top 2 sports for a mini preview
  const getTopSports = () => {
    if (!gym?.sport || gym.sport.length === 0) return [];
    return gym.sport.slice(0, 2).map((s) => s.name);
  };

  return (
    <Card
      onClick={() =>
        navigate(`/gyms/${gym?._id}/${gym?.name?.replaceAll(" ", "-")}`)
      }
      onMouseEnter={() => !isMobile && setHoveredCard(gym._id)}
      onMouseLeave={() => !isMobile && setHoveredCard(null)}
      elevation={hoveredCard === gym._id ? 8 : 2}
      sx={{
        height: getCardHeight(),
        position: "relative",
        borderRadius: { xs: 3, sm: 4 },
        overflow: "hidden",
        cursor: "pointer",
        transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
        transform:
          !isMobile && hoveredCard === gym._id ? "translateY(-8px)" : "none",
        bgcolor: "background.paper",
      }}
    >
      {/* Background Image with Zoom Effect */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          // backgroundImage: `url(${import.meta.env.VITE_URL + gym?.gallery?.[0]})`,
          // backgroundSize: "cover",
          // backgroundPosition: "center",
          transition: "transform 0.7s ease",
          transform:
            !isMobile && hoveredCard === gym._id ? "scale(1.08)" : "scale(1)",
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.8) 100%)",
            transition: "opacity 0.3s ease",
            opacity: hoveredCard === gym._id ? 0.9 : 0.7,
          },
        }}
      >
        <img
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          src={import.meta.env.VITE_URL + gym?.gallery?.[0]}
          alt={gym?.name || "notfound"}
        />
      </Box>

      {/* Top Badges */}
      <Stack
        direction="row"
        gap={0.5}
        sx={{
          position: "absolute",
          top: 16,
          left: 16,
          zIndex: 2,
        }}
      >
        {gym?.isDiscount && gym?.discount_text && (
          <Chip
            label="تخفیف‌دار"
            size="small"
            sx={{
              bgcolor: theme.palette.error.main,
              color: "#fff",
              fontWeight: 700,
              fontSize: "0.7rem",
              backdropFilter: "blur(4px)",
              boxShadow: "0 4px 12px rgba(211, 47, 47, 0.3)",
              border: "1px solid rgba(255,255,255,0.2)",
            }}
          />
        )}
        {gym?.genderType && gym.genderType !== "mixed" && (
          <Chip
            label={
              gym.genderType === "women"
                ? "ویژه بانوان"
                : gym.genderType === "men"
                  ? "ویژه آقایان"
                  : "آقایان و بانوان"
            }
            size="small"
            sx={{
              bgcolor:
                gym.genderType === "women"
                  ? "#9c27b0" // بنفش برای بانوان
                  : gym.genderType === "men"
                    ? "#0288d1" // آبی برای آقایان
                    : "#2e7d32", // سبز تیره برای سانس‌های مجزا (آقایان و بانوان)
              color: "#fff",
              fontWeight: 600,
              fontSize: "0.7rem",
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
              border: "1px solid rgba(255,255,255,0.2)",
            }}
          />
        )}
      </Stack>

      {/* Bottom Content - Glassmorphism */}
      <Box
        sx={{
          position: "absolute",
          bottom: 12,
          left: 12,
          right: 12,
          p: 2,
          borderRadius: 3,
          background: "rgba(20, 20, 20, 0.45)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          border: "1px solid rgba(255, 255, 255, 0.15)",
          zIndex: 2,
          transition: "all 0.3s ease",
        }}
      >
        <Stack direction="row" gap={2} alignItems="center" mb={1.5}>
          {/* Circular Logo */}
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: "50%",
              bgcolor: "background.paper",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 10px rgba(0,0,0,0.3)",
              overflow: "hidden",
              border: "2px solid #fff",
              flexShrink: 0,
            }}
          >
            {gym?.logo ? (
              <img
                src={`${import.meta.env.VITE_URL + gym.logo}`}
                alt={gym.name}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <Typography variant="h6" fontWeight="bold" color="primary">
                {gym?.name?.charAt(0) || "G"}
              </Typography>
            )}
          </Box>

          {/* Title & Location */}
          <Box flexGrow={1}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                color: "#fff",
                lineHeight: 1.2,
                mb: 0.5,
                textShadow: "0 2px 4px rgba(0,0,0,0.5)",
                fontSize: "1rem",
              }}
            >
              {gym?.name || "باشگاه ورزشی"}
            </Typography>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <LocationIcon sx={{ fontSize: 14, color: "#ffb74d" }} />
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.8)", fontWeight: 500 }}
              >
                {getLocationString()}
              </Typography>
            </Stack>
          </Box>
        </Stack>

        {/* Sports Preview Chips */}
        <Stack direction="row" gap={1} mb={1.5} flexWrap="wrap">
          {getTopSports().map((sport, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                bgcolor: "rgba(255,255,255,0.1)",
                px: 1,
                py: 0.25,
                borderRadius: 1.5,
              }}
            >
              <SportIcon sx={{ fontSize: 12, color: "#90caf9" }} />
              <Typography variant="caption" color="#fff" fontSize="0.65rem">
                {sport}
              </Typography>
            </Box>
          ))}
        </Stack>

        {/* Price & Action */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            pt: 1.5,
            borderTop: "1px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          <Box>
            <Typography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.6)", display: "block" }}
            >
              شروع قیمت از
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                color: "#69f0ae",
                fontSize: "1rem",
              }}
            >
              {getPrice()}
              <span
                style={{ fontSize: "0.7rem", fontWeight: 400, color: "#fff" }}
              >
                تومان
              </span>
            </Typography>
          </Box>

          <Button
            variant="contained"
            disableElevation
            sx={{
              borderRadius: 2,
              px: 2,
              py: 0.75,
              bgcolor: theme.palette.primary.main,
              color: "#fff",
              fontWeight: 700,
              "&:hover": {
                bgcolor: theme.palette.primary.dark,
              },
            }}
          >
            مشاهده
            <ChevronLeftIcon sx={{ ml: -0.5, fontSize: 18 }} />
          </Button>
        </Stack>
      </Box>
    </Card>
  );
}
